import { memo } from "react";
import { Tooltip } from "antd";
import { getFrameworkLetters } from "utils/frameworkConfig";
import "./FrameworkCircles.css";

/**
 * Default style configuration
 */
const DEFAULT_STYLE_CONFIG = {
  width: 25,
  height: 25,
  fontSize: 11,
  fontWeight: 600,
  borderRadius: "50%",
  backgroundColor: null, // Will use framework's background_color
  color: null, // Will use framework's font_color
  border: null,
  flexShrink: 0,
};

/**
 * Individual framework circle
 */
const FrameworkCircle = memo(({
  framework,
  styleConfig,
  customClassName = "",
  showTooltip = true,
}) => {
  const bg = styleConfig.backgroundColor || framework?.background_color || "#6b7280";
  const fg = styleConfig.color || framework?.font_color || "#ffffff";

  const style = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: styleConfig.width,
    height: styleConfig.height,
    borderRadius: styleConfig.borderRadius,
    fontSize: styleConfig.fontSize,
    fontWeight: styleConfig.fontWeight,
    backgroundColor: bg,
    color: fg,
    border: styleConfig.border,
    flexShrink: styleConfig.flexShrink,
  };

  const name = framework?.framework_name || framework?.name || "";
  const circle = (
    <div
      className={`framework-circle ${customClassName}`.trim()}
      style={style}
    >
      {getFrameworkLetters(framework?.framework_name || framework?.name || "")}
    </div>
  );

  if (showTooltip && name) {
    return <Tooltip title={name}>{circle}</Tooltip>;
  }

  return circle;
});

FrameworkCircle.displayName = "FrameworkCircle";

/**
 * FrameworkCircles - Provides flexible framework circle display
 * 
 * Displays framework circles with customizable styling.
 * All framework data comes from the framework objects, but styles can be overridden.
 * 
 * @param {Array|Object} frameworks - Single framework or array of frameworks to display
 * @param {Object} styleOverrides - Style overrides for all circles
 * @param {Object} styleOverrides.backgroundColor - Override framework background color
 * @param {Object} styleOverrides.color - Override framework font color
 * @param {Object} styleOverrides.border - Border style (e.g., "1px solid #000")
 * @param {Object} styleOverrides.width - @deprecated No longer has any effect
 * @param {Object} styleOverrides.height - @deprecated No longer has any effect
 * @param {Object} styleOverrides.fontSize - @deprecated No longer has any effect
 * @param {Object} styleOverrides.fontWeight - @deprecated No longer has any effect
 * @param {Object} styleOverrides.borderRadius - @deprecated No longer has any effect
 * @param {Object} perFrameworkStyles - Style overrides per framework ID - { "framework-123": { backgroundColor: "#ff0000" } }
 * @param {Object} perFrameworkClassNames - Additional CSS classes per framework ID
 * @param {string} containerClassName - Custom class for the container
 * @param {number} gap - Gap between circles in pixels - Default: 4px
 * @param {boolean} showTooltips - Whether to show framework name on hover
 * @param {ReactNode} emptyDisplay - Custom render when no frameworks to display
 * @param {string} emptyText - Text to display when empty - Defaults to "--"
 * @param {Function} renderCustomCircle - Optional custom render function for individual circles
 */
const FrameworkCircles = memo(({
  frameworks = null,
  styleOverrides = {},
  perFrameworkStyles = {},
  perFrameworkClassNames = {},
  containerClassName = "",
  gap = 4,
  showTooltips = true,
  emptyDisplay = null,
  emptyText = "--",
  renderCustomCircle = null,
}) => {
  // Normalize frameworks to array
  const frameworkArray = frameworks
    ? (Array.isArray(frameworks) ? frameworks : [frameworks])
    : null;

  // Check if we have any frameworks to display
  if (!frameworkArray || frameworkArray.length === 0) {
    return emptyDisplay !== null 
      ? emptyDisplay 
      : <span className="framework-circles-empty">{emptyText}</span>;
  }

  // Destructure out deprecated properties so they are accepted but have no effect,
  // then merge only the remaining active overrides with the default config.
  const {
    width: _width,
    height: _height,
    fontSize: _fontSize,
    fontWeight: _fontWeight,
    borderRadius: _borderRadius,
    ...activeStyleOverrides
  } = styleOverrides;

  const mergedStyleConfig = {
    ...DEFAULT_STYLE_CONFIG,
    ...activeStyleOverrides,
  };

  return (
    <div 
      className={`framework-circles-container ${containerClassName}`.trim()}
      style={{ display: "inline-flex", gap: `${gap}px`, alignItems: "center" }}
    >
      {frameworkArray.map((framework, index) => {
        if (!framework) return null;

        const frameworkId = framework.id || framework._id || index;
        const frameworkStyles = perFrameworkStyles[frameworkId] || {};
        const frameworkClassName = perFrameworkClassNames[frameworkId] || "";

        // Merge framework-specific styles
        const finalStyleConfig = {
          ...mergedStyleConfig,
          ...frameworkStyles,
        };

        // Custom render function
        if (renderCustomCircle) {
          return renderCustomCircle({
            key: frameworkId,
            framework,
            styleConfig: finalStyleConfig,
            customClassName: frameworkClassName,
            showTooltip: showTooltips,
          });
        }

        // Default render
        return (
          <FrameworkCircle
            key={frameworkId}
            framework={framework}
            styleConfig={finalStyleConfig}
            customClassName={frameworkClassName}
            showTooltip={showTooltips}
          />
        );
      })}
    </div>
  );
});

FrameworkCircles.displayName = "FrameworkCircles";

export default FrameworkCircles;